/**
 * IntakeComposer
 *
 * The Plan surface's input. Deliberately not ChatInput: this is a planning
 * composer, so it carries no chat affordances (slash commands, message
 * editing) and clears only once the submission has actually succeeded.
 */

import { Component, createSignal } from "solid-js";
import { Button } from "../ui/Button";

export interface IntakeComposerProps {
  /** Resolves true when the submission succeeded and the field should clear */
  onSubmit: (value: string) => Promise<boolean>;
  disabled?: boolean;
  placeholder?: string;
  /** Label for the submit button */
  submitLabel?: string;
  /** Taller field for the empty state's opening prompt */
  hero?: boolean;
  autofocus?: boolean;
  /**
   * Pre-fills the field. Read once when the composer mounts — the caller
   * gates rendering until the value is known. Deliberately not synced
   * reactively: the composer clears itself on a successful submit, and an
   * effect would put the seed straight back.
   */
  initialValue?: string;
  "aria-label"?: string;
}

export const IntakeComposer: Component<IntakeComposerProps> = (props) => {
  const [value, setValue] = createSignal(props.initialValue ?? "");

  const submit = async () => {
    const trimmed = value().trim();
    if (!trimmed || props.disabled) return;
    if (await props.onSubmit(trimmed)) {
      setValue("");
    }
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      void submit();
    }
  };

  return (
    <div class="flex flex-col gap-2">
      <textarea
        value={value()}
        onInput={(e) => setValue(e.currentTarget.value)}
        onKeyDown={handleKeyDown}
        placeholder={props.placeholder}
        disabled={props.disabled}
        aria-label={props["aria-label"] ?? "Describe your initiative"}
        autofocus={props.autofocus}
        rows={props.hero ? 4 : 2}
        class={
          "w-full resize-none rounded-md border border-line bg-surface px-4 py-3 " +
          "text-ink placeholder:text-ink-3 " +
          "disabled:bg-sunken disabled:text-ink-3 disabled:cursor-not-allowed " +
          (props.hero ? "text-base" : "text-sm")
        }
      />
      <div class="flex items-center justify-between">
        <span class="text-xs text-ink-3">
          Enter to send · Shift+Enter for a new line
        </span>
        <Button
          variant="primary"
          size={props.hero ? "md" : "sm"}
          onClick={() => void submit()}
          disabled={props.disabled || !value().trim()}
        >
          {props.submitLabel ?? "Send"}
        </Button>
      </div>
    </div>
  );
};

export default IntakeComposer;
